/**
 * Passkeys utility — mirrors the web's generateCredentials / passkeyAuth flow
 * but calls the same backend endpoints as the web.
 *
 * Requires a custom EAS dev build (react-native-passkeys is a native module).
 * Does NOT work inside Expo Go — all exports degrade gracefully when the
 * native module is unavailable so the app/SettingsScreen never crashes.
 */
import { apiGet, apiPost, apiDelete, apiPatch } from './apiClient';
import { store } from '../store';

// Lazy-load the native module so that missing/unlinked native code in Expo Go
// never crashes the JS module at import time.
let _create: ((opts: any) => Promise<any>) | undefined;
let _get: ((opts: any) => Promise<any>) | undefined;
let _isSupported: (() => boolean) | undefined;

try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const mod = require('react-native-passkeys');
  _create = mod.create;
  _get = mod.get;
  _isSupported = mod.isSupported;
} catch {
  _create = undefined;
  _get = undefined;
  _isSupported = undefined;
}

// The relying party domain. WebAuthn requires the rpId to be the current
// document's hostname (or a registrable suffix of it) — sending a literal
// `app.eleplan.com` while the page is loaded from `development.eleplan.org`
// makes Chrome reject the call with NotAllowedError before the OS even
// prompts. On native (iOS/Android) the rpId is constant because the
// associatedDomains entitlement and AASA file are pinned to app.eleplan.com.
//
// Resolution order:
//   1. Web (RN-Web / Electron renderer): use `window.location.hostname`
//      so the call works on dev.eleplan.org / development.eleplan.org /
//      localhost / app.eleplan.com without further config.
//   2. Native: fall back to the production app domain that matches
//      iOS associatedDomains + the AASA file.
const NATIVE_RP_ID = 'app.eleplan.com';
const NATIVE_RP_ORIGIN = 'https://app.eleplan.com';

function getRuntimeRpId(): string {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  return NATIVE_RP_ID;
}

function getRuntimeRpOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return NATIVE_RP_ORIGIN;
}

const RP_ID = getRuntimeRpId();
const RP_ORIGIN = getRuntimeRpOrigin();

// --- Helpers -----------------------------------------------------------------

function getIds(): { accountId: string; cognitoId: string } {
  const state = store.getState();
  const accountId = state.session?.account?.account_id || '';
  const cognitoId = state.authentication?.user?.cognito_id || '';
  return { accountId, cognitoId };
}

/** Converts a string to a URL-safe base64 string (for the WebAuthn user.id field) */
function strToBase64Url(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// --- API calls ---------------------------------------------------------------

async function getAuthChallenge(verify = false): Promise<string> {
  const { accountId, cognitoId } = getIds();
  const res = await apiGet(
    `/users/get-auth-challenge/${accountId}/${cognitoId}?verify=${verify}`,
  );
  return res?.payload || res?.challenge || res;
}

async function storeCredential(credential: any, challenge: string): Promise<boolean> {
  const { accountId, cognitoId } = getIds();
  const res = await apiPost(
    `/users/store-credential/${accountId}/${cognitoId}`,
    { credential, challenge },
    { 'x-passkey-origin': RP_ORIGIN },
  );
  return !!res?.success;
}

async function verifyCredential(credential: any, challenge: string): Promise<boolean> {
  const { accountId, cognitoId } = getIds();
  const res = await apiPost(
    `/users/verify-credential/${accountId}/${cognitoId}`,
    { credential, challenge },
    { 'x-passkey-origin': RP_ORIGIN },
  );
  return !!res?.success;
}

export async function deletePasskey(credentialId: string): Promise<boolean> {
  const { accountId, cognitoId } = getIds();
  const res = await apiDelete(
    `/users/delete-passkey/${credentialId}/${accountId}/${cognitoId}`,
  );
  return !!res?.success;
}

export async function editPasskeyName(credentialId: string, name: string): Promise<boolean> {
  const { accountId, cognitoId } = getIds();
  const res = await apiPatch(
    `/users/edit-passkey/${credentialId}/${accountId}/${cognitoId}`,
    { name },
  );
  return !!res?.success;
}

// --- Registration flow -------------------------------------------------------

/**
 * Register a new passkey for the current user.
 * Mirrors the web's generateCredentials() utility.
 */
export async function registerPasskey(): Promise<{ success: boolean; message?: string }> {
  if (!isPasskeySupported()) {
    return { success: false, message: 'Passkeys are not supported on this device or build.' };
  }

  const state = store.getState();
  const user = state.authentication?.user;
  const displayName = user
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
    : 'eleplan user';
  const userId = user?.cognito_id || 'unknown';

  try {
    const challenge = await getAuthChallenge(false);

    const credential = await _create!({
      rp: { id: RP_ID, name: 'eleplan' },
      user: {
        id: strToBase64Url(userId),
        name: user?.email || userId,
        displayName,
      },
      challenge,
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },   // ES256
        { type: 'public-key', alg: -257 },  // RS256
      ],
      authenticatorSelection: {
        userVerification: 'required',
        residentKey: 'required',
      },
      attestation: 'none',
      timeout: 60000,
    });

    if (!credential) {
      return { success: false, message: 'Passkey creation was cancelled.' };
    }

    const stored = await storeCredential(credential, challenge);
    if (!stored) {
      return { success: false, message: 'Could not store passkey on server.' };
    }

    return { success: true };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Passkey registration failed.' };
  }
}

// --- Authentication flow -----------------------------------------------------

/**
 * Authenticate using an existing passkey for the current user.
 * Mirrors the web's passkeyAuth() utility.
 * Returns true on success.
 */
export async function authenticateWithPasskey(
  // Shape matches `/users/get-user`: `{ id, origin, ... }` flat. The credential
  // id IS the top-level `id` — there is no nested `credential.id`. Reading the
  // nested shape silently returned an empty allowCredentials list and forced
  // every passkey login on web to fall through to PIN.
  passkeys: Array<{ id?: string; origin?: string }>,
): Promise<boolean> {
  if (!isPasskeySupported()) return false;

  // On web, narrow to credentials registered against this exact origin —
  // matches the web frontend's `passkeys.filter(p => p?.origin === origin)`
  // behaviour. Passkeys registered for app.eleplan.com won't unlock on
  // development.eleplan.org (different origin, different rp), and feeding
  // them to navigator.credentials.get() yields a long timeout + a confusing
  // NotAllowedError instead of a graceful fall-through.
  const currentOrigin = getRuntimeRpOrigin();
  const isWeb = typeof window !== 'undefined' && !!window.location?.origin;
  const candidates = isWeb
    ? passkeys.filter((pk) => !pk?.origin || pk.origin === currentOrigin)
    : passkeys;

  const allowedIds = candidates
    .filter((pk): pk is { id: string; origin?: string } => !!pk?.id)
    .map((pk) => ({ id: pk.id, type: 'public-key' as const }));

  // Nothing registered for this origin — let the caller fall through to
  // PIN / passkey-creation rather than firing an empty allowCredentials
  // request that silently times out.
  if (!allowedIds.length) return false;

  try {
    const challenge = await getAuthChallenge(true);

    const assertion = await _get!({
      challenge,
      rpId: RP_ID,
      allowCredentials: allowedIds,
      userVerification: 'required',
      timeout: 60000,
    });

    if (!assertion) return false;

    const normalized = {
      ...assertion,
      credentialId: assertion.id,
    };

    return await verifyCredential(normalized, challenge);
  } catch {
    return false;
  }
}

/**
 * Get a fresh passkey assertion (biometric prompt) without round-tripping
 * through /verify-credential. Returns `{ credential, challenge }` for the
 * caller to embed in the body of a privileged request (e.g. SSM reveal /
 * write / delete), where the receiving handler runs its own verification.
 *
 * Resolves with `null` if the user dismisses the prompt, has no eligible
 * passkey for this origin, or the native module isn't linked.
 */
export async function getPasskeyAssertion(
  // Flat shape from `/users/get-user`; see authenticateWithPasskey above for
  // why the nested `{ credential: { id } }` form is wrong.
  passkeys: Array<{ id?: string; origin?: string }>,
): Promise<{ credential: any; challenge: string } | null> {
  if (!isPasskeySupported()) return null;

  const currentOrigin = getRuntimeRpOrigin();
  const isWeb = typeof window !== 'undefined' && !!window.location?.origin;
  const candidates = isWeb
    ? passkeys.filter((pk) => !pk?.origin || pk.origin === currentOrigin)
    : passkeys;

  const allowedIds = candidates
    .filter((pk): pk is { id: string; origin?: string } => !!pk?.id)
    .map((pk) => ({ id: pk.id, type: 'public-key' as const }));

  if (!allowedIds.length) return null;

  try {
    const challenge = await getAuthChallenge(true);
    const assertion = await _get!({
      challenge,
      rpId: RP_ID,
      allowCredentials: allowedIds,
      userVerification: 'required',
      timeout: 60000,
    });
    if (!assertion) return null;
    return {
      credential: { ...assertion, credentialId: assertion.id },
      challenge,
    };
  } catch {
    return null;
  }
}

// --- Sign in with a passkey --------------------------------------------------

export type PasskeyLoginResult =
  | { success: true; username: string; cognitoId: string; authResult: any }
  | { success: false; cancelled?: boolean; message: string };

const isCancellation = (err: any): boolean => {
  const name = String(err?.name || '');
  const msg = String(err?.message || '').toLowerCase();
  return name === 'NotAllowedError' || name === 'AbortError' || /cancel|abort|dismiss/.test(msg);
};

/**
 * "Sign in with a passkey" (ED-2799): no email, no password. Asks the backend
 * for a one-time challenge, lets the OS pick any discoverable passkey for this
 * relying party (created on the web or in the app and synced through iCloud
 * Keychain / Google Password Manager), then exchanges the assertion for a
 * Cognito session through /auth/passkey-login/verify. Both endpoints are
 * public; the challenge is single-use and expires in five minutes.
 */
export async function passkeyLogin(): Promise<PasskeyLoginResult> {
  if (!isPasskeySupported()) {
    return { success: false, message: 'Passkeys are not supported on this device or build.' };
  }

  let challenge: string;
  try {
    const res = await apiPost('/auth/passkey-login/challenge', {}, { 'x-passkey-origin': RP_ORIGIN });
    challenge = res?.payload?.challenge;
    if (!res?.success || typeof challenge !== 'string') {
      return { success: false, message: res?.message || 'Could not start passkey sign-in.' };
    }
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not start passkey sign-in.' };
  }

  let assertion: any;
  try {
    // An empty allowCredentials list asks for a discoverable credential, so the
    // OS shows the account picker instead of needing an email first.
    assertion = await _get!({
      challenge,
      rpId: RP_ID,
      allowCredentials: [],
      userVerification: 'required',
      timeout: 60000,
    });
  } catch (err: any) {
    if (isCancellation(err)) return { success: false, cancelled: true, message: 'Passkey sign-in was cancelled.' };
    return { success: false, message: err?.message || 'Could not read a passkey on this device.' };
  }
  if (!assertion) return { success: false, cancelled: true, message: 'Passkey sign-in was cancelled.' };

  try {
    const res = await apiPost(
      '/auth/passkey-login/verify',
      { credential: { ...assertion, credentialId: assertion.id }, challenge },
      { 'x-passkey-origin': RP_ORIGIN },
    );
    if (!res?.success || !res.authentication || !res.username) {
      return { success: false, message: res?.message || 'Could not sign in with this passkey.' };
    }
    return { success: true, username: String(res.username), cognitoId: String(res.cognito_id || ''), authResult: res.authentication };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not sign in with this passkey.' };
  }
}

/** Returns true only when the react-native-passkeys native module is linked and available. */
export function isPasskeySupported(): boolean {
  try {
    return typeof _isSupported === 'function' ? _isSupported() : false;
  } catch {
    return false;
  }
}
